import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { getHdFixedStarBySide } from './hd-fixed-stars.metadata';
import { HD_CONSTELLATION_NAMES, HD_FIXED_STAR_SKY, HdFixedStarSkyPosition } from './hd-fixed-star-sky.data';
import { HdFixedStarSkyCatalogService } from './hd-fixed-star-sky-catalog.service';
import { SKY_H, SKY_W, SKY_ZOOM_IN_FACTOR, SkyDot, SkyNamedLabel, buildSkyGeometry, formatBayer, magRadius } from './hd-fixed-star-sky.util';

/**
 * Tekst wordt in het smalle rechterpaneel mee-verkleind met de SVG; daarom in
 * `compact` een grotere letterschaal zodat sub-tekst en legende leesbaar blijven.
 * In de grote dialog (schaal 1) is de tekst al goed. Parametriseerbaar.
 */
const SKY_COMPACT_FONT_SCALE = 1.7;

interface DualTab {
  readonly label: string;
  readonly second: boolean;
  readonly active: boolean;
}

interface SkyChartVm {
  readonly loaded: boolean;
  readonly title: string;
  readonly subtitle: string;
  readonly graticule: readonly string[];
  readonly lines: readonly string[];
  readonly dots: readonly SkyDot[];
  readonly target: { readonly cx: number; readonly cy: number } | null;
  /** Straal van de doelster op natuurlijke (magnitude-)grootte. */
  readonly targetR: number;
  readonly named: readonly SkyNamedLabel[];
  readonly badge: string; // 'Sterrenhoop' / 'Nevelgebied' / ''
  readonly labelText: string;
  readonly labelSub: string;
  readonly pill: {
    readonly x: number; readonly y: number; readonly leaderX: number; readonly leaderY: number;
    readonly w: number; readonly h: number;
  } | null;
  readonly orientation: string;
  readonly dualTabs: readonly DualTab[];
}

/**
 * Zelf-gerenderde sterrenkaart voor één fixed star (aanpak B, volledig offline):
 * het sterrenbeeld met lijnen en het sterrenveld (magnitude ≤ 6) uit de
 * gebundelde catalogus, met de doelster uitgelicht. Data uit
 * {@link HD_FIXED_STAR_SKY}; catalogus via {@link HdFixedStarSkyCatalogService}.
 * In `compact`-modus is de kaart klikbaar om te vergroten (`enlarge`-output).
 */
@Component({
  selector: 'hd-fixed-star-sky-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <figure class="sky" [class.sky--compact]="variant() === 'compact'">
      @if (vm().dualTabs.length > 0) {
        <div class="sky__tabs">
          @for (tab of vm().dualTabs; track tab.label) {
            <button type="button" class="sky__tab" [class.sky__tab--active]="tab.active"
                    (click)="selectDual(tab.second); $event.stopPropagation()">{{ tab.label }}</button>
          }
        </div>
      }

      <div class="sky__frame" [class.sky__frame--clickable]="variant() === 'compact'"
           (click)="onFrameClick()"
           [attr.role]="variant() === 'compact' ? 'button' : null"
           [attr.aria-label]="variant() === 'compact' ? vm().title : null">
        <svg [attr.viewBox]="'0 0 ' + W + ' ' + H" xmlns="http://www.w3.org/2000/svg"
             preserveAspectRatio="xMidYMid meet" role="img" [attr.aria-label]="vm().title">
          <defs>
            <radialGradient [attr.id]="'sky-' + uid" cx="46%" cy="42%" r="78%">
              <stop offset="0%" stop-color="#141d38" /><stop offset="55%" stop-color="#0a1024" /><stop offset="100%" stop-color="#05070f" />
            </radialGradient>
            <radialGradient [attr.id]="'hl-' + uid" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stop-color="#ffe6c2" stop-opacity="0.9" /><stop offset="32%" stop-color="#ffab5c" stop-opacity="0.55" /><stop offset="100%" stop-color="#ff8a3c" stop-opacity="0" />
            </radialGradient>
          </defs>
          <rect [attr.width]="W" [attr.height]="H" [attr.fill]="'url(#sky-' + uid + ')'" />

          <g stroke="#5f7bc0" stroke-opacity="0.09" stroke-width="1" fill="none">
            @for (d of vm().graticule; track $index) { <path [attr.d]="d" /> }
          </g>
          <g stroke="#9fb6ff" stroke-opacity="0.4" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round">
            @for (d of vm().lines; track $index) { <path [attr.d]="d" /> }
          </g>
          <g>
            @for (dot of vm().dots; track $index) {
              @if (dot.glow > 0) { <circle [attr.cx]="dot.cx" [attr.cy]="dot.cy" [attr.r]="dot.glow" [attr.fill]="dot.fill" opacity="0.14" /> }
              <circle [attr.cx]="dot.cx" [attr.cy]="dot.cy" [attr.r]="dot.r" [attr.fill]="dot.fill" />
            }
          </g>

          @if (vm().target; as t) {
            @if (badgeRing()) {
              <circle [attr.cx]="t.cx" [attr.cy]="t.cy" r="120" fill="none" stroke="#ffcf8a" stroke-opacity="0.32" stroke-width="1.2" stroke-dasharray="4 6" />
            }
            <circle class="sky__halo" [attr.cx]="t.cx" [attr.cy]="t.cy" r="30" [attr.fill]="'url(#hl-' + uid + ')'" />
            <circle [attr.cx]="t.cx" [attr.cy]="t.cy" r="27" fill="none" stroke="#ffb257" stroke-opacity="0.42" stroke-width="1" stroke-dasharray="3 6" />
            <circle [attr.cx]="t.cx" [attr.cy]="t.cy" r="16" fill="none" stroke="#ffcf8a" stroke-opacity="0.7" stroke-width="1.3" stroke-dasharray="2 4" />
            <circle [attr.cx]="t.cx" [attr.cy]="t.cy" [attr.r]="vm().targetR" fill="#ffffff" />
            <circle [attr.cx]="t.cx" [attr.cy]="t.cy" [attr.r]="vm().targetR" fill="none" stroke="#ff8a3c" stroke-opacity="0.85" stroke-width="1" />
            @if (vm().pill; as pill) {
              <line [attr.x1]="t.cx" [attr.y1]="t.cy" [attr.x2]="pill.leaderX" [attr.y2]="pill.leaderY" stroke="#ffcf8a" stroke-opacity="0.5" />
              <g [attr.transform]="'translate(' + pill.x + ',' + pill.y + ')'">
                <rect x="0" y="0" [attr.rx]="8 * fs()" [attr.width]="pill.w" [attr.height]="pill.h" fill="#12182c" stroke="#ffb257" stroke-opacity="0.55" />
                <text [attr.x]="12 * fs()" [attr.y]="19 * fs()" fill="#ffe0b4" [attr.font-size]="14 * fs()" font-weight="600">{{ vm().labelText }}</text>
                <text [attr.x]="12 * fs()" [attr.y]="35 * fs()" fill="#9fb0d6" [attr.font-size]="11 * fs()">{{ vm().labelSub }}</text>
              </g>
            }
          }

          <g class="sky__named">
            @for (n of vm().named; track n.l) {
              <circle [attr.cx]="n.x" [attr.cy]="n.y" r="7" fill="transparent" pointer-events="all">
                <title>{{ n.t }}</title>
              </circle>
              @if (namesShown()) {
                <text [attr.x]="n.anchorEnd ? n.x - 6 : n.x + 6" [attr.y]="n.y - 5"
                      [attr.text-anchor]="n.anchorEnd ? 'end' : 'start'" [attr.font-size]="10 * fs()"
                      fill="#b7c2e0" paint-order="stroke" stroke="#070a14" stroke-width="2.5"
                      stroke-opacity="0.85" stroke-linejoin="round">{{ n.l }}</text>
              }
            }
          </g>

          <text x="22" [attr.y]="14 + 13 * fs()" fill="#e6ecff" [attr.font-size]="15 * fs()" font-weight="600">{{ vm().title }}</text>
          <text x="22" [attr.y]="16 + 28 * fs()" fill="#7f92bd" [attr.font-size]="11.5 * fs()">{{ vm().subtitle }}</text>
          @if (vm().badge) {
            <text [attr.x]="W - 22" [attr.y]="14 + 13 * fs()" fill="#ffcf8a" [attr.font-size]="11.5 * fs()" text-anchor="end">{{ vm().badge }}</text>
          }
          <text [attr.x]="W - 22" [attr.y]="H - 10 * fs()" fill="#5f6f97" [attr.font-size]="10.5 * fs()" text-anchor="end">{{ vm().orientation }}</text>
        </svg>

        <button type="button" class="sky__zoom" (click)="toggleZoom(); $event.stopPropagation()"
                [attr.title]="zoomedIn() ? zoomOutLabel() : zoomInLabel()"
                [attr.aria-label]="zoomedIn() ? zoomOutLabel() : zoomInLabel()">
          <i class="fas" [class.fa-magnifying-glass-minus]="zoomedIn()" [class.fa-magnifying-glass-plus]="!zoomedIn()"></i>
        </button>
        <button type="button" class="sky__names" [class.sky__names--active]="namesShown()"
                (click)="toggleNames(); $event.stopPropagation()"
                [attr.title]="namesShown() ? namesOffLabel() : namesOnLabel()"
                [attr.aria-label]="namesShown() ? namesOffLabel() : namesOnLabel()">
          <i class="fas fa-tag"></i>
        </button>
        @if (variant() === 'compact') {
          <span class="sky__expand" aria-hidden="true"><i class="fas fa-up-right-and-down-left-from-center"></i></span>
        }
        @if (!vm().loaded) {
          <span class="sky__loading">…</span>
        }
      </div>
    </figure>
  `,
  styles: `
    .sky { margin: 0; display: flex; flex-direction: column; gap: 6px; }
    .sky__tabs { display: flex; gap: 6px; }
    .sky__tab {
      padding: 3px 10px; border: 1px solid #d9cdb4; border-radius: 999px;
      background: #f7f3ea; color: inherit; font: inherit; font-size: 12px; font-weight: 600; cursor: pointer;
    }
    .sky__tab--active { background: #12182c; color: #ffe0b4; border-color: #12182c; }
    .sky__frame { position: relative; border-radius: 14px; overflow: hidden; line-height: 0; }
    .sky__frame--clickable { cursor: zoom-in; }
    svg { width: 100%; height: auto; display: block; }
    .sky__expand {
      position: absolute; top: 8px; right: 8px; width: 26px; height: 26px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 7px; background: rgba(18, 24, 44, 0.72); color: #cdd8f2; font-size: 12px;
      opacity: 0; transition: opacity 0.15s ease;
    }
    .sky__frame--clickable:hover .sky__expand { opacity: 1; }
    .sky__zoom {
      position: absolute; left: 8px; bottom: 8px; width: 26px; height: 26px;
      display: flex; align-items: center; justify-content: center;
      border: 0; border-radius: 7px; background: rgba(18, 24, 44, 0.66); color: #cdd8f2;
      font-size: 12px; cursor: pointer; opacity: 0.75; transition: opacity 0.15s ease, background 0.15s ease;
    }
    .sky__zoom:hover { opacity: 1; background: rgba(18, 24, 44, 0.92); }
    .sky__names {
      position: absolute; left: 42px; bottom: 8px; width: 26px; height: 26px;
      display: flex; align-items: center; justify-content: center;
      border: 0; border-radius: 7px; background: rgba(18, 24, 44, 0.66); color: #cdd8f2;
      font-size: 12px; cursor: pointer; opacity: 0.75; transition: opacity 0.15s ease, background 0.15s ease;
    }
    .sky__names:hover { opacity: 1; background: rgba(18, 24, 44, 0.92); }
    .sky__names--active { opacity: 1; background: #ffb257; color: #12182c; }
    .sky__names--active:hover { background: #ffc985; color: #12182c; }
    .sky__loading { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: #7f92bd; font-size: 22px; }
    @keyframes sky-pulse { 0%, 100% { opacity: 0.85; } 50% { opacity: 0.4; } }
    .sky__halo { animation: sky-pulse 3.4s ease-in-out infinite; transform-origin: center; }
    @media (prefers-reduced-motion: reduce) { .sky__halo { animation: none; } }
  `,
})
export class HdFixedStarSkyChartComponent {
  readonly side = input.required<number>();
  readonly variant = input<'compact' | 'large'>('compact');
  readonly enlarge = output<void>();

  protected readonly W = SKY_W;
  protected readonly H = SKY_H;
  protected readonly uid = `${HdFixedStarSkyChartComponent.nextId++}`;
  private static nextId = 0;

  private readonly catalogService = inject(HdFixedStarSkyCatalogService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly useSecond = signal(false);
  /** `null` = het standaardniveau voor de variant (compact = ingezoomd, large = uitgezoomd). */
  private readonly zoomChoice = signal<'in' | 'out' | null>(null);

  protected readonly zoomedIn = computed(() => {
    const choice = this.zoomChoice();
    return choice != null ? choice === 'in' : this.variant() === 'compact';
  });
  protected readonly zoomInLabel = computed(() => this.text('skyChart.zoomIn'));
  protected readonly zoomOutLabel = computed(() => this.text('skyChart.zoomOut'));
  /** Letterschaal: groter in het compacte paneel, 1 in de grote dialog. */
  protected readonly fs = computed(() => (this.variant() === 'compact' ? SKY_COMPACT_FONT_SCALE : 1));

  protected readonly namesShown = signal(false);
  protected readonly namesOnLabel = computed(() => this.text('skyChart.namesOn'));
  protected readonly namesOffLabel = computed(() => this.text('skyChart.namesOff'));

  constructor() {
    this.catalogService.ensureLoaded();
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected toggleZoom(): void {
    this.zoomChoice.set(this.zoomedIn() ? 'out' : 'in');
  }

  protected toggleNames(): void {
    this.namesShown.update(shown => !shown);
  }

  protected badgeRing = computed(() => {
    const entry = HD_FIXED_STAR_SKY[this.side()];
    return entry?.kind === 'cluster' || entry?.kind === 'nebula';
  });

  protected readonly vm = computed<SkyChartVm>(() => {
    const entry = HD_FIXED_STAR_SKY[this.side()];
    const star = getHdFixedStarBySide(this.side());
    const catalog = this.catalogService.catalog();
    const useSecond = this.useSecond();
    const position: HdFixedStarSkyPosition | undefined = useSecond && entry?.second ? entry.second : entry;

    const dualTabs: DualTab[] = entry?.kind === 'dual' && entry.second
      ? [
          { label: HD_CONSTELLATION_NAMES[entry.con] ?? entry.con, second: false, active: !useSecond },
          { label: HD_CONSTELLATION_NAMES[entry.second.con] ?? entry.second.con, second: true, active: useSecond },
        ]
      : [];

    if (!entry || !star || !position) {
      return this.emptyVm(star?.name ?? '', dualTabs);
    }

    const conName = HD_CONSTELLATION_NAMES[position.con] ?? position.con;
    const base: Omit<SkyChartVm, 'graticule' | 'lines' | 'dots' | 'target' | 'named' | 'pill' | 'loaded'> = {
      title: `${star.name} — ${conName}`,
      subtitle: `${this.text('panel.sideNumber')} ${star.side} · ${formatBayer(position.bayer)}`,
      targetR: magRadius(position.mag),
      badge: entry.kind === 'cluster' ? this.text('skyChart.clusterTag')
        : entry.kind === 'nebula' ? this.text('skyChart.nebulaTag') : '',
      labelText: star.name,
      labelSub: `${formatBayer(position.bayer)} · ${conName}`,
      orientation: this.text('skyChart.orientation'),
      dualTabs,
    };

    if (!catalog) {
      return { ...base, loaded: false, graticule: [], lines: [], dots: [], target: null, named: [], pill: null };
    }

    const fov = this.zoomedIn() ? position.fov * SKY_ZOOM_IN_FACTOR : position.fov;
    const geometry = buildSkyGeometry(position, catalog, fov);
    return { ...base, loaded: true, ...geometry, pill: this.pillFor(geometry.target, this.fs()) };
  });

  protected selectDual(second: boolean): void {
    this.useSecond.set(second);
  }

  protected onFrameClick(): void {
    if (this.variant() === 'compact') {
      this.enlarge.emit();
    }
  }

  private pillFor(target: { cx: number; cy: number } | null, scale: number): SkyChartVm['pill'] {
    if (!target) {
      return null;
    }
    const w = 196 * scale;
    const h = 44 * scale;
    const right = target.cx > 560;
    const leaderX = right ? target.cx - 30 : target.cx + 30;
    const y = Math.max(58, Math.min(this.H - 8 - h, target.cy + 40));
    const x = right ? leaderX - w : leaderX;
    return { x, y, leaderX, leaderY: y + 10, w, h };
  }

  private emptyVm(name: string, dualTabs: readonly DualTab[]): SkyChartVm {
    return {
      loaded: false, title: name, subtitle: '', graticule: [], lines: [], dots: [], target: null, named: [],
      targetR: 0, badge: '', labelText: '', labelSub: '', pill: null, orientation: '', dualTabs,
    };
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('fixedStars', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('fixedStars', key, 'nl');
  }
}
